import { createSlice, createSelector } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { createEagerSelector, eagerMetaFactory } from "../common/reducers/pendingUpdateSlice";

const epcrVitalSlice = createSlice({
    name: 'epcrVitals',
    initialState: {
        data: {},
        defs: []
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const vital of ensureArray(action.payload)) {
                if (!state.data[vital.pcrid]) {
                    state.data[vital.pcrid] = [vital];
                } else {
                    let idx = state.data[vital.pcrid].findIndex(a => vital.id === a.id);
                    if (idx >= 0) {
                        state.data[vital.pcrid].splice(idx, 1, vital);
                    } else {
                        state.data[vital.pcrid] = [...state.data[vital.pcrid], vital];
                    }
                }
            }
        },
        remove: (state, action) => {
            for (const vital of ensureArray(action.payload)) {
                let idx = state.data[vital.pcrid].findIndex(a => a.id === vital.id);
                if (idx >= 0) {
                    state.data[vital.pcrid].splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => epcrVitalSlice.getInitialState());
    }
});

// #region network operations
const eagerMeta = eagerMetaFactory(epcrVitalSlice.name);
const fetchEpcrVitals = pcrid => network => network.get('/api/v2/epcrvitals/' + encodeURIComponent(pcrid), epcrVitalSlice.actions.addOrUpdate);
const fetchEpcrVitalDefs = (network) => network.get('/api/v2/vitals', epcrVitalSlice.actions.setDefs);
export const addEpcrVital = (network, data) => network.put('/api/v2/epcrvital', data, epcrVitalSlice.actions.addOrUpdate,  eagerMeta(data.id, { fk: data.pcrid })); // TODO better failure
export const removeEpcrVital = (network, id, pcrid) => network.delete('/api/v2/epcrvital/' + encodeURIComponent(id), epcrVitalSlice.actions.remove, eagerMeta(id, { fk: pcrid })); // TODO better failure
export const updateEpcrVital = (network, data) => network.post('/api/v2/epcrvital', data, epcrVitalSlice.actions.addOrUpdate, eagerMeta(data.id, { fk: data.pcrid })) // TODO better failure??
// #endregion

// #region selectors
const selectEpcrVitalDefs = state => state.epcrVitals.defs;
const selectAllEpcrVitals = state => state.epcrVitals.data;

const eagerSelector = createEagerSelector(epcrVitalSlice.name, { mode: 'mapToArray' });
const selectEpcrVitalsByID = eagerSelector(createSelector([selectAllEpcrVitals, (vitals, pcrid) => pcrid], (vitals, pcrid) => vitals[pcrid] ?? []));
const selectEpcrVitals = pcrid  => state => selectEpcrVitalsByID(state, pcrid);
// #endregion

// #region listeners
const listen = {
    epcrVitalAddedv2: (vital, { dispatch }) => dispatch(epcrVitalSlice.actions.addOrUpdate(vital)),
    epcrVitalUpdatedv2: (vital, { dispatch }) => dispatch(epcrVitalSlice.actions.addOrUpdate(vital)),
    epcrVitalRemovedv2: (vital, { dispatch }) => dispatch(epcrVitalSlice.actions.remove(vital))
}
// #endregion 

export const [useEpcrVitals, realizeEpcrVitals] = dataHookFactory(selectEpcrVitals, initMapFactory(fetchEpcrVitals, listen, pcrid => pcrid?.toString()));
export const [useEpcrVitalDefs, realizeEpcrVitalDefs] = dataHookFactory(selectEpcrVitalDefs, { run: fetchEpcrVitalDefs });

const reducerDetails = { name: epcrVitalSlice.name, reducer: epcrVitalSlice.reducer };
export default reducerDetails;

